import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { TokenMintError } from "../datastore/LangGraphTokenManager";
import { ChatMessage, ChatThreadSummary, chatDataStore, formatThreadTitle } from "../datastore/ChatDataStore";
import { shareRecipientByEmail, usersByStytchIds } from "../datastore/UserDataStore";
import { useUserContext } from "../context/UserContext";
import { toCsv, downloadCsv } from "./fmsCsv";
import { buildThreadDeepLink, CsvDownloadView, FmsChatThreadRowView, SENDING_PHASE, ThinkingSegment, fmsChatDisplayConverter } from "./FmsChatDisplayConverter";

export interface ShareMemberView {
    stytchUserId: string;
    name: string;
}

export interface ShareDialogState {
    threadId: string;
    threadTitle: string;
    // The email being typed to add a recipient.
    emailInput: string;
    // Generic "not shareable" message after a failed add, else null.
    addError: string | null;
    // True while an add is resolving (disables the Add control).
    isAdding: boolean;
    // Existing shared-with people (removable); the owner is never listed.
    currentMembers: ShareMemberView[];
    // General access mode of the thread (Restricted vs anyone-with-link).
    linkShared: boolean;
    linkUrl: string;
    linkCopied: boolean;
}

export interface FmsChatStateManager {
    ownedThreadRows: FmsChatThreadRowView[];
    sharedThreadRows: FmsChatThreadRowView[];
    activeThreadId: string | null;
    messages: ChatMessage[];
    conversationTitle: string;
    statusLabel: string | null;
    isBusy: boolean;
    draft: string;
    error: string | null;
    blocked: boolean;
    pendingDeleteTitle: string | null;
    liveThinking: ThinkingSegment[] | null;
    thinkingByMsgIdx: { [idx: number]: ThinkingSegment[] };
    csvDownloadByMsgIdx: { [idx: number]: CsvDownloadView };
    onDownloadCsv: (msgIdx: number) => void;
    expandedMsgIdx: { [idx: number]: boolean };
    onDraftChange: (value: string) => void;
    onSubmit: () => void;
    onOpenThread: (threadId: string) => void;
    onStartNew: () => void;
    onRequestDelete: (threadId: string) => void;
    onConfirmDelete: () => void;
    onCancelDelete: () => void;
    onToggleThoughts: (msgIdx: number) => void;
    renamingThreadId: string | null;
    renameDraft: string;
    onStartRename: (threadId: string) => void;
    onRenameDraftChange: (value: string) => void;
    onCommitRename: () => void;
    onCancelRename: () => void;
    isViewingSharedThread: boolean;
    deepLinkNoAccess: boolean;
    deepLinkLoadFailed: boolean;
    shareDialog: ShareDialogState | null;
    onOpenShare: (threadId: string) => void;
    onSetLinkShared: (linkShared: boolean) => void;
    onCloseShare: () => void;
    onShareEmailChange: (value: string) => void;
    onAddByEmail: () => void;
    onRemoveMember: (stytchUserId: string) => void;
    onCopyShareLink: () => void;
}

export const useFmsChatStateManager = (): FmsChatStateManager => {
    const { threadId: deepLinkThreadId } = useParams<{ threadId?: string }>();
    const deepLinkOwner = new URLSearchParams(useLocation().search).get("owner");
    const history = useHistory();
    const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState("");
    // Raw progress phase: a node name, the SENDING_PHASE sentinel, or null
    // (idle). The converter maps it to a label; isBusy is phase !== null.
    const [phase, setPhase] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Server-side mint rejection (403/401 after refresh): the client-side
    // group check passed but the server disagreed; the view shows the
    // invite-only state instead of a transient error line.
    const [blocked, setBlocked] = useState(false);
    // The thread awaiting delete confirmation; non-null shows the dialog.
    const [pendingDelete, setPendingDelete] = useState<ChatThreadSummary | null>(null);
    // Inline rename: the thread whose title is being edited (null = none) plus
    // the working draft. renameRef mirrors the live edit so commit (which can
    // fire from onBlur right after Esc cancels) reads the truth, and cancel can
    // null it so a trailing blur does not re-commit.
    const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
    const [renameDraft, setRenameDraft] = useState("");
    const renameRef = useRef<{ id: string; title: string } | null>(null);
    // Live thinking for the in-flight turn (expanded, streaming); null when idle.
    const [liveThinking, setLiveThinking] = useState<ThinkingSegment[] | null>(null);
    // Retained per answered turn, keyed by assistant message index. Hydrated from
    // the persisted transcript on load; for the just-answered turn, falls back to
    // the live-captured segments if the backend has not persisted thinking yet.
    const [thinkingByMsgIdx, setThinkingByMsgIdx] = useState<{ [idx: number]: ThinkingSegment[] }>({});
    const [expandedMsgIdx, setExpandedMsgIdx] = useState<{ [idx: number]: boolean }>({});
    // Synchronous accumulation buffer (state is async; deltas arrive fast).
    const liveThinkingRef = useRef<ThinkingSegment[]>([]);
    const openSeqRef = useRef(0);
    // Current user's own Stytch id (from the app-wide user query), used to split
    // owned vs shared-with-me threads and to gate the view-only composer.
    const { user } = useUserContext();
    // Non-null in practice: the page is RampartAuthorized-gated and minting a
    // chat token requires a stytchUserId. If it were null, every owned thread
    // would misclassify as shared-with-me (view-only).
    const myStytchId = user?.stytchUserId ?? null;
    // Resolved display names for owner/member Stytch ids (owners of
    // shared-with-me threads + members of owned threads), fetched by id - never
    // a directory.
    const [nameByStytchId, setNameByStytchId] = useState<{ [stytchId: string]: string }>({});
    // The thread whose Share dialog is open (null = closed) + the add-by-email
    // input and its generic rejection message.
    const [shareDialogThreadId, setShareDialogThreadId] = useState<string | null>(null);
    const [shareEmailInput, setShareEmailInput] = useState("");
    const [shareAddError, setShareAddError] = useState<string | null>(null);
    const [shareAddPending, setShareAddPending] = useState(false);
    // A /chat/:threadId link to a thread the user cannot access renders a
    // uniform "no access" pane (same as not-found, so it cannot probe existence).
    const [deepLinkNoAccess, setDeepLinkNoAccess] = useState(false);
    const [deepLinkLoadFailed, setDeepLinkLoadFailed] = useState(false);
    // The deep-linked thread isn't in `threads` until refreshThreads reconciles,
    // so its owner is unknown briefly. Track it to gate the composer fail-closed
    // (view-only) during that window.
    const [deepLinkedThreadId, setDeepLinkedThreadId] = useState<string | null>(null);
    // Consume the deep-link param exactly once; history.replace below re-renders
    // with no param, and this guard prevents the effect from re-firing.
    const deepLinkConsumedRef = useRef(false);
    const [shareLinkCopied, setShareLinkCopied] = useState(false);

    const appendThinking = (step: string, delta: string) => {
        const segs = liveThinkingRef.current;
        const last = segs[segs.length - 1];
        if (last && last.step === step) {
            last.text += delta;
        } else {
            segs.push({ step, text: delta });
        }
        setLiveThinking(segs.map(s => ({ ...s })));
    };

    const handleFailure = (err: unknown) => {
        liveThinkingRef.current = [];
        setLiveThinking(null);
        setPhase(null);
        if (err instanceof TokenMintError && (err.status === 403 || err.status === 401)) {
            setBlocked(true);
            return;
        }
        setError(err instanceof Error ? err.message : "Something went wrong");
    };

    const refreshThreads = useCallback(() => {
        chatDataStore.listThreads().then(setThreads).catch(() => setThreads([]));
    }, []);

    useEffect(() => {
        refreshThreads();
    }, [refreshThreads]);

    // Resolve display names for the Stytch ids that appear as owners or members
    // across the loaded threads. Bounded to those ids (never a directory) and
    // keyed on a stable join so it only refetches when the id set changes.
    const relevantStytchIds = useMemo(() => {
        const ids = new Set<string>();
        for (const t of threads) {
            if (t.ownerId) {
                ids.add(t.ownerId);
            }
            for (const m of t.memberIds) {
                ids.add(m);
            }
        }
        return Array.from(ids).sort();
    }, [threads]);
    const relevantIdsKey = relevantStytchIds.join(",");

    useEffect(() => {
        if (relevantStytchIds.length === 0) {
            return;
        }
        // Non-fatal: on failure, names fall back to a generic label rather than
        // blocking chat.
        usersByStytchIds(relevantStytchIds)
            .then(recipients => {
                const map: { [stytchId: string]: string } = {};
                for (const r of recipients) {
                    map[r.stytchUserId] = `${r.firstName} ${r.lastName}`.trim();
                }
                setNameByStytchId(map);
            })
            .catch(() => { /* keep prior names */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [relevantIdsKey]);

    const openThread = (threadId: string) => {
        // Guard mid-run: onDone would land the in-flight run's transcript in
        // the newly selected view.
        if (phase !== null) {
            return;
        }
        const seq = ++openSeqRef.current;
        setActiveThreadId(threadId);
        setError(null);
        setDeepLinkNoAccess(false);
        setDeepLinkLoadFailed(false);
        setDeepLinkedThreadId(null);
        // Retained thinking is keyed by message index, so it must be dropped
        // when the transcript is swapped for another thread — otherwise the
        // previous thread's Thoughts would render against this thread's message
        // at the same index.
        setThinkingByMsgIdx({});
        setExpandedMsgIdx({});
        chatDataStore.getThreadMessages(threadId)
            .then(transcript => {
                if (openSeqRef.current === seq) {
                    setMessages(transcript);
                    setThinkingByMsgIdx(fmsChatDisplayConverter.thinkingByMsgIdx(transcript));
                }
            })
            .catch(err => {
                if (openSeqRef.current === seq) {
                    handleFailure(err);
                }
            });
    };

    const startNewThread = () => {
        // Guard mid-run: same race as openThread.
        if (phase !== null) {
            return;
        }
        // Invalidate any in-flight openThread fetch so its transcript cannot
        // land over the fresh conversation.
        openSeqRef.current += 1;
        setActiveThreadId(null);
        setMessages([]);
        setError(null);
        setDeepLinkNoAccess(false);
        setDeepLinkLoadFailed(false);
        setDeepLinkedThreadId(null);
        // Drop retained thinking from the previous thread (keyed by message
        // index; would otherwise leak onto the fresh conversation).
        setThinkingByMsgIdx({});
        setExpandedMsgIdx({});
    };

    useEffect(() => {
        if (!deepLinkThreadId || deepLinkConsumedRef.current || phase !== null) {
            return;
        }
        deepLinkConsumedRef.current = true;
        const seq = ++openSeqRef.current;
        setError(null);
        setThinkingByMsgIdx({});
        setExpandedMsgIdx({});
        chatDataStore.loadThreadForDeepLink(deepLinkThreadId)
            .then(async transcript => {
                if (openSeqRef.current !== seq) {
                    return;
                }
                if (transcript === null && deepLinkOwner) {
                    // Not a member, but the link carries an owner claim: ask the
                    // self-serve server for a link-shared (ephemeral, view-only)
                    // read. The thread never joins `threads`, so the existing
                    // deepLinkedThreadId machinery keeps the composer disabled.
                    transcript = await chatDataStore.loadSharedThreadState(deepLinkThreadId, deepLinkOwner);
                    if (openSeqRef.current !== seq) {
                        return;
                    }
                }
                if (transcript === null) {
                    setDeepLinkNoAccess(true);
                } else {
                    setActiveThreadId(deepLinkThreadId);
                    setDeepLinkedThreadId(deepLinkThreadId);
                    setMessages(transcript);
                    setThinkingByMsgIdx(fmsChatDisplayConverter.thinkingByMsgIdx(transcript));
                }
            })
            .catch(() => {
                if (openSeqRef.current === seq) {
                    // A genuine load failure (not the 403/404 no-access case):
                    // show a dedicated pane that forces "New conversation"
                    // instead of dropping the user into a usable-looking empty
                    // composer that silently starts a brand-new thread.
                    setDeepLinkLoadFailed(true);
                }
            })
            .finally(() => history.replace("/chat"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkThreadId]);

    const performDelete = (threadId: string) => {
        chatDataStore.deleteThread(threadId)
            .then(() => {
                if (threadId === activeThreadId) {
                    startNewThread();
                }
                refreshThreads();
            })
            .catch(handleFailure);
    };

    const startRename = (threadId: string) => {
        const current = threads.find(t => t.threadId === threadId)?.title ?? "";
        renameRef.current = { id: threadId, title: current };
        setRenameDraft(current);
        setRenamingThreadId(threadId);
    };

    const changeRenameDraft = (value: string) => {
        if (renameRef.current !== null) {
            renameRef.current.title = value;
        }
        setRenameDraft(value);
    };

    const cancelRename = () => {
        renameRef.current = null;
        setRenamingThreadId(null);
    };

    const commitRename = () => {
        const edit = renameRef.current;
        renameRef.current = null;
        setRenamingThreadId(null);
        // Null when cancel already ran (e.g. Esc, then a trailing blur).
        if (edit === null) {
            return;
        }
        const title = edit.title.trim();
        // An empty title is treated as a cancel rather than clearing the name.
        if (!title) {
            return;
        }
        // Optimistic; reconcile from the server on success, and on failure too
        // so a rejected rename reverts the optimistic title to the server's
        // truth instead of leaving it stale next to the error.
        setThreads(prev => prev.map(t => (t.threadId === edit.id ? { ...t, title } : t)));
        chatDataStore.renameThread(edit.id, title)
            .then(refreshThreads)
            .catch((err) => {
                refreshThreads();
                handleFailure(err);
            });
    };

    const submit = async () => {
        const question = draft.trim();
        if (!question || phase !== null) {
            return;
        }
        // Shared-with-me and not-yet-reconciled deep-linked threads are
        // view-only. Reuse isViewingSharedThread so this guard and the view's
        // disabled composer can never disagree during the deep-link
        // transitional window (before threads reconciles the deep-linked id).
        if (isViewingSharedThread) {
            return;
        }
        // Invalidate any in-flight openThread fetch so a stale transcript
        // cannot clobber the optimistic message below.
        openSeqRef.current += 1;
        setDraft("");
        setError(null);
        setMessages(prev => [...prev, { role: "human", content: question }]);
        setPhase(SENDING_PHASE);
        liveThinkingRef.current = [];
        setLiveThinking(null);
        try {
            const threadId = activeThreadId ?? await chatDataStore.createThread();
            if (activeThreadId === null) {
                setActiveThreadId(threadId);
                // Show the new thread, titled by its first question, immediately.
                // The server derives the same title once the message lands;
                // onDone's refreshThreads reconciles this optimistic row.
                setThreads(prev => [
                    {
                        threadId,
                        createdAt: new Date().toISOString(),
                        title: formatThreadTitle(question),
                        ownerId: myStytchId,
                        memberIds: myStytchId ? [myStytchId] : [],
                        linkShared: false
                    },
                    ...prev
                ]);
            }
            await chatDataStore.sendMessage(threadId, question, {
                onNodeUpdate: (node) => setPhase(node),
                onThinking: appendThinking,
                onDone: async () => {
                    // The stream only carries progress; the transcript is
                    // re-fetched so the server stays the source of truth.
                    try {
                        const transcript = await chatDataStore.getThreadMessages(threadId);
                        setMessages(transcript);
                        const hydrated = fmsChatDisplayConverter.thinkingByMsgIdx(transcript);
                        // If the backend didn't persist thinking for this answer
                        // (e.g. not yet deployed), fall back to what streamed live
                        // so the just-finished turn still shows its Thoughts.
                        const segs = liveThinkingRef.current;
                        const aiIdx = transcript.length - 1;
                        if (hydrated[aiIdx] === undefined && segs.length > 0) {
                            hydrated[aiIdx] = segs.map(s => ({ ...s }));
                        }
                        setThinkingByMsgIdx(hydrated);
                        liveThinkingRef.current = [];
                        setLiveThinking(null);
                        // Reconcile the sidebar with the server: the optimistic
                        // new-thread row picks up its real created_at, and any
                        // custom/derived title is confirmed here.
                        refreshThreads();
                    } catch (err) {
                        handleFailure(err);
                    } finally {
                        setPhase(null);
                    }
                },
                onError: handleFailure
            });
        } catch (err) {
            // Restore the typed question so a failed send doesn't force a re-type.
            setDraft(question);
            handleFailure(err);
        }
    };

    // Derived once per messages change (not per render): the filename's Eastern
    // timestamp is captured here and stays stable until the transcript changes.
    const csvDownloadByMsgIdx = useMemo(
        () => fmsChatDisplayConverter.csvDownloadByMsgIdx(messages),
        [messages]
    );

    const downloadCsvForMessage = (msgIdx: number) => {
        // Guard on the download view (the same gate that governs whether the
        // button renders), so `view` is never undefined at the downloadCsv call.
        const view = csvDownloadByMsgIdx[msgIdx];
        const rt = messages[msgIdx]?.resultTable;
        if (!view || !rt) {
            return;
        }
        downloadCsv(view.filename, toCsv(rt.columns, rt.rows));
    };

    const activeThread = threads.find(t => t.threadId === activeThreadId) ?? null;
    // Once the thread is in `threads`, use its real owner. Before that (a fresh
    // deep-link load), fail closed: an unreconciled deep-linked thread is treated
    // as view-only so the composer can't be used on a thread shared with us. A
    // brand-new thread (activeThreadId null) is unaffected and stays editable.
    const isViewingSharedThread = activeThread !== null
        ? fmsChatDisplayConverter.isSharedWithMe(activeThread, myStytchId)
        : activeThreadId !== null && activeThreadId === deepLinkedThreadId;

    const { ownedThreadRows, sharedThreadRows } = useMemo(() => {
        const buildRow = (t: ChatThreadSummary): FmsChatThreadRowView => ({
            ...fmsChatDisplayConverter.threadRow(t),
            sharedByLabel: fmsChatDisplayConverter.sharedByLabel(t, myStytchId, nameByStytchId)
        });
        return {
            ownedThreadRows: threads.filter(t => !fmsChatDisplayConverter.isSharedWithMe(t, myStytchId)).map(buildRow),
            sharedThreadRows: threads.filter(t => fmsChatDisplayConverter.isSharedWithMe(t, myStytchId)).map(buildRow)
        };
    }, [threads, myStytchId, nameByStytchId]);

    const shareThreadModel = threads.find(t => t.threadId === shareDialogThreadId) ?? null;
    const shareDialog: ShareDialogState | null = shareThreadModel === null ? null : {
        threadId: shareThreadModel.threadId,
        threadTitle: shareThreadModel.title ?? "Untitled",
        emailInput: shareEmailInput,
        addError: shareAddError,
        isAdding: shareAddPending,
        currentMembers: fmsChatDisplayConverter
            .removableMemberIds(shareThreadModel.memberIds, shareThreadModel.ownerId, myStytchId)
            .map(id => ({ stytchUserId: id, name: nameByStytchId[id] ?? "Unknown user" })),
        linkShared: shareThreadModel.linkShared,
        linkUrl: buildThreadDeepLink(window.location.origin, shareThreadModel.threadId, shareThreadModel.ownerId),
        linkCopied: shareLinkCopied
    };

    const applyMembers = (threadId: string, memberIds: string[]) => {
        // Optimistic; reconcile from the server on success and on failure (so a
        // rejected write reverts to the server's truth).
        setThreads(prev => prev.map(t => (t.threadId === threadId ? { ...t, memberIds } : t)));
        chatDataStore.setThreadMembers(threadId, memberIds)
            .then(refreshThreads)
            .catch((err) => {
                refreshThreads();
                handleFailure(err);
            });
    };

    return {
        ownedThreadRows,
        sharedThreadRows,
        activeThreadId,
        messages,
        conversationTitle: fmsChatDisplayConverter.conversationTitle(messages),
        statusLabel: fmsChatDisplayConverter.nodeStatusLabel(phase),
        isBusy: phase !== null,
        draft,
        error,
        blocked,
        pendingDeleteTitle: pendingDelete ? (pendingDelete.title ?? "Untitled") : null,
        liveThinking,
        thinkingByMsgIdx,
        csvDownloadByMsgIdx,
        onDownloadCsv: downloadCsvForMessage,
        expandedMsgIdx,
        onDraftChange: setDraft,
        onSubmit: submit,
        onOpenThread: openThread,
        onStartNew: startNewThread,
        onRequestDelete: (threadId) => {
            // Guard mid-run, consistent with openThread. The view passes only
            // the id; the manager owns the model lookup (no model object leaks
            // into the view).
            if (phase !== null) {
                return;
            }
            const target = threads.find(t => t.threadId === threadId);
            if (target) {
                setPendingDelete(target);
            }
        },
        onConfirmDelete: () => {
            const target = pendingDelete;
            setPendingDelete(null);
            if (target !== null) {
                performDelete(target.threadId);
            }
        },
        onCancelDelete: () => setPendingDelete(null),
        onToggleThoughts: (i: number) => setExpandedMsgIdx(prev => ({ ...prev, [i]: !prev[i] })),
        renamingThreadId,
        renameDraft,
        onStartRename: startRename,
        onRenameDraftChange: changeRenameDraft,
        onCommitRename: commitRename,
        onCancelRename: cancelRename,
        isViewingSharedThread,
        deepLinkNoAccess,
        deepLinkLoadFailed,
        shareDialog,
        onOpenShare: (threadId: string) => {
            if (phase !== null) {
                return;
            }
            setShareEmailInput("");
            setShareAddError(null);
            setShareLinkCopied(false);
            setShareDialogThreadId(threadId);
        },
        onSetLinkShared: (linkShared: boolean) => {
            if (shareDialogThreadId === null) {
                return;
            }
            chatDataStore.setThreadLinkShared(shareDialogThreadId, linkShared)
                .then(refreshThreads)
                .catch(() => setShareAddError("Could not update access right now. Please try again."));
        },
        onCloseShare: () => {
            setShareDialogThreadId(null);
            setShareEmailInput("");
            setShareAddError(null);
            setShareLinkCopied(false);
        },
        onShareEmailChange: (value: string) => {
            setShareEmailInput(value);
            if (shareAddError !== null) {
                setShareAddError(null);
            }
        },
        onAddByEmail: () => {
            const email = shareEmailInput.trim();
            const t = threads.find(th => th.threadId === shareDialogThreadId);
            if (email === "" || t === undefined || shareAddPending) {
                return;
            }
            setShareAddPending(true);
            setShareAddError(null);
            shareRecipientByEmail(email)
                .then(recipient => {
                    if (recipient === null) {
                        // One generic message for every rejection (unknown email,
                        // never logged in, or not in the beta) - no probing.
                        setShareAddError("You can only share with people who have FMS chat access.");
                        return;
                    }
                    // Show the added member's name at once; the next thread
                    // refresh re-resolves it anyway.
                    setNameByStytchId(prev => ({
                        ...prev,
                        [recipient.stytchUserId]: `${recipient.firstName} ${recipient.lastName}`.trim()
                    }));
                    setShareEmailInput("");
                    applyMembers(t.threadId, Array.from(new Set([...t.memberIds, recipient.stytchUserId])));
                })
                .catch(() => setShareAddError("Could not share right now. Please try again."))
                .finally(() => setShareAddPending(false));
        },
        onRemoveMember: (stytchUserId: string) => {
            if (phase !== null) {
                return;
            }
            const t = threads.find(th => th.threadId === shareDialogThreadId);
            // The owner (and the current user, who owns a null-owner legacy
            // thread) is never removable - also excluded from currentMembers.
            if (t && stytchUserId !== t.ownerId && stytchUserId !== myStytchId) {
                applyMembers(t.threadId, t.memberIds.filter(id => id !== stytchUserId));
            }
        },
        onCopyShareLink: () => {
            if (shareDialogThreadId === null) {
                return;
            }
            const t = threads.find(th => th.threadId === shareDialogThreadId);
            const url = buildThreadDeepLink(window.location.origin, shareDialogThreadId, t?.ownerId);
            // Optional chaining: clipboard is undefined in insecure contexts;
            // the dialog still shows the URL for manual copy.
            navigator.clipboard?.writeText(url)
                .then(() => setShareLinkCopied(true))
                .catch(() => { /* manual copy fallback: the field shows the URL */ });
        }
    };
};
